import React, { useState, useEffect, useCallback } from "react";
import { getOrderByStatus } from "../../../../api/order.api";
import moment from "moment";

interface StatusStats {
  status: string;
  count: number;
  color: string;
  bgColor: string;
  icon: JSX.Element;
  label: string;
}

interface DayStats {
  date: string;
  stats: StatusStats[];
  totalOrders: number;
}

const MiniDashboard: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dayStats, setDayStats] = useState<DayStats | null>(null);

  const statusConfig = [
    {
      status: "ordered",
      label: "Waiting",
      color: "#4169E1",
      bgColor: "#E4EBFF",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 0C3.13438 0 0 3.13438 0 7C0 10.8656 3.13438 14 7 14C10.8656 14 14 10.8656 14 7C14 3.13438 10.8656 0 7 0ZM7 12.6875C3.85625 12.6875 1.3125 10.1437 1.3125 7C1.3125 3.85625 3.85625 1.3125 7 1.3125C10.1437 1.3125 12.6875 3.85625 12.6875 7C12.6875 10.1437 10.1437 12.6875 7 12.6875Z"
            fill="currentColor"
          />
          <path
            d="M7 3.5C6.63906 3.5 6.34375 3.79531 6.34375 4.15625V7C6.34375 7.36094 6.63906 7.65625 7 7.65625H9.625C9.98594 7.65625 10.2812 7.36094 10.2812 7C10.2812 6.63906 9.98594 6.34375 9.625 6.34375H7.65625V4.15625C7.65625 3.79531 7.36094 3.5 7 3.5Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      status: "in-progress",
      label: "In Progress",
      color: "#FF8C00",
      bgColor: "#FFF3E0",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.5625 7C13.5625 10.3827 10.8827 13.0625 7.5 13.0625C4.11726 13.0625 1.4375 10.3827 1.4375 7C1.4375 3.61726 4.11726 0.9375 7.5 0.9375C10.8827 0.9375 13.5625 3.61726 13.5625 7ZM2.8125 7C2.8125 9.62371 4.87629 11.6875 7.5 11.6875V2.3125C4.87629 2.3125 2.8125 4.37629 2.8125 7Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      status: "done",
      label: "Done",
      color: "#4FB580",
      bgColor: "#E8F5F0",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 0C3.13438 0 0 3.13438 0 7C0 10.8656 3.13438 14 7 14C10.8656 14 14 10.8656 14 7C14 3.13438 10.8656 0 7 0ZM10.4781 5.36563L6.50312 9.34063C6.39844 9.44531 6.25781 9.5 6.10938 9.5C5.96094 9.5 5.82031 9.44531 5.71563 9.34063L3.52188 7.14688C3.30469 6.92969 3.30469 6.57969 3.52188 6.3625C3.73906 6.14531 4.08906 6.14531 4.30625 6.3625L6.10938 8.16563L9.69375 4.58125C9.91094 4.36406 10.2609 4.36406 10.4781 4.58125C10.6953 4.79844 10.6953 5.14844 10.4781 5.36563Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      status: "picked",
      label: "Picked Up",
      color: "#059669",
      bgColor: "#D1FAE5",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.25 3.5H10.5V2.625C10.5 1.58906 9.66094 0.75 8.625 0.75H5.375C4.33906 0.75 3.5 1.58906 3.5 2.625V3.5H1.75C1.33594 3.5 1 3.83594 1 4.25V5C1 5.19062 1.15938 5.35 1.35 5.35H2.00781L2.45469 12.1469C2.48906 12.7516 2.99844 13.25 3.60781 13.25H10.3922C11.0016 13.25 11.5109 12.7516 11.5453 12.1469L11.9922 5.35H12.65C12.8406 5.35 13 5.19062 13 5V4.25C13 3.83594 12.6641 3.5 12.25 3.5ZM9.25 3.5H4.75V2.625C4.75 2.27812 5.02812 2 5.375 2H8.625C8.97188 2 9.25 2.27812 9.25 2.625V3.5Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      status: "rejected",
      label: "Rejected",
      color: "#DC2626",
      bgColor: "#FEE2E2",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 0C3.13438 0 0 3.13438 0 7C0 10.8656 3.13438 14 7 14C10.8656 14 14 10.8656 14 7C14 3.13438 10.8656 0 7 0ZM9.73906 8.95469C9.95625 9.17187 9.95625 9.52187 9.73906 9.73906C9.63438 9.84375 9.49375 9.89844 9.34531 9.89844C9.19688 9.89844 9.05625 9.84375 8.95156 9.73906L7 7.78906L5.04844 9.73906C4.94375 9.84375 4.80313 9.89844 4.65469 9.89844C4.50625 9.89844 4.36563 9.84375 4.26094 9.73906C4.04375 9.52187 4.04375 9.17187 4.26094 8.95469L6.21094 7L4.26094 5.04844C4.04375 4.83125 4.04375 4.48125 4.26094 4.26406C4.47813 4.04688 4.82813 4.04688 5.04531 4.26406L7 6.21406L8.95156 4.26406C9.16875 4.04688 9.51875 4.04688 9.73594 4.26406C9.95313 4.48125 9.95313 4.83125 9.73594 5.04844L7.78594 7L9.73906 8.95469Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      status: "cancel",
      label: "Cancelled",
      color: "#EF4444",
      bgColor: "#FEE2E2",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 0C3.13438 0 0 3.13438 0 7C0 10.8656 3.13438 14 7 14C10.8656 14 14 10.8656 14 7C14 3.13438 10.8656 0 7 0ZM9.625 8.34219C9.84219 8.55938 9.84219 8.90938 9.625 9.12656C9.51719 9.23438 9.37656 9.28906 9.22812 9.28906C9.07969 9.28906 8.93906 9.23438 8.83125 9.12656L7 7.29531L5.16875 9.12656C5.06094 9.23438 4.92031 9.28906 4.77188 9.28906C4.62344 9.28906 4.48281 9.23438 4.375 9.12656C4.15781 8.90938 4.15781 8.55938 4.375 8.34219L6.20625 6.51094L4.375 4.67969C4.15781 4.4625 4.15781 4.1125 4.375 3.89531C4.59219 3.67813 4.94219 3.67813 5.15937 3.89531L7 5.72656L8.83125 3.89531C9.04844 3.67813 9.39844 3.67813 9.61562 3.89531C9.83281 4.1125 9.83281 4.4625 9.61562 4.67969L7.78437 6.51094L9.625 8.34219Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ];

  const fetchDailyStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const today = moment().format("YYYY-MM-DD");
      const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");

      const promises = statusConfig.map((config) =>
        getOrderByStatus(config.status, today, tomorrow)
      );

      const results = await Promise.all(promises);

      const stats: StatusStats[] = statusConfig.map((config, index) => ({
        status: config.status,
        label: config.label,
        count: results[index].length,
        color: config.color,
        bgColor: config.bgColor,
        icon: config.icon,
      }));

      const totalOrders = stats.reduce((sum, stat) => sum + stat.count, 0);

      setDayStats({
        date: today,
        stats,
        totalOrders,
      });
    } catch (error) {
      console.error("Error fetching daily stats:", error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDailyStats();
    
    // Optional: Auto-refresh every 5 minutes
    const interval = setInterval(fetchDailyStats, 300000);
    return () => clearInterval(interval);
  }, [fetchDailyStats]);

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Minimized Button */}
      <div className="mx-16 my-12">
        <button
          onClick={toggleModal}
          className="w-full bg-white rounded-xl shadow-md border border-gray-200 px-16 py-14 hover:bg-gray-50 transition-all hover:shadow-lg font-nunito"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-12">
              <div className="flex items-center justify-center w-32 h-32 bg-primary-P50 rounded-lg">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 4H14M2 8H14M2 12H14"
                    stroke="#4169E1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M2 2H14C14.5523 2 15 2.44772 15 3V13C15 13.5523 14.5523 14 14 14H2C1.44772 14 1 13.5523 1 13V3C1 2.44772 1.44772 2 2 2Z"
                    stroke="#4169E1"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="text-14 font-semibold text-gray-900">
                  Today's Dashboard
                </h3>
                <p className="text-12 text-gray-500">
                  {moment().format("MMM D, YYYY")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-12">
              {dayStats && (
                <div className="flex items-center gap-8">
                  <span className="text-12 text-gray-500">Total:</span>
                  <span className="text-18 font-bold text-primary-P300">
                    {dayStats.totalOrders}
                  </span>
                </div>
              )}
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12.5L10 7.5L15 12.5"
                  stroke="#4169E1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </button>
      </div>

      {/* Bottom Sheet Modal */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={closeModal}
          />

          {/* Bottom Sheet */}
          <div
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl z-50 font-nunito animate-slide-up"
            style={{
              maxHeight: "85vh",
              animation: "slideUp 0.3s ease-out",
            }}
          >
            {/* Handle Bar */}
            <div className="flex justify-center pt-12 pb-8">
              <div className="w-40 h-4 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-20 py-12 border-b border-netural-N40">
              <div className="flex items-center gap-12">
                <div className="flex items-center justify-center w-40 h-40 bg-gradient-to-br from-primary-P500 to-primary-P300 rounded-xl">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 5H18M2 10H18M2 15H18"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-18 font-bold text-primary-onligth">
                    Today's Dashboard
                  </h2>
                  <p className="text-12 text-secondary-S700">
                    {moment().format("dddd, MMMM D, YYYY")}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="w-32 h-32 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L13 13M1 13L13 1"
                    stroke="#666"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-20 pb-20" style={{ maxHeight: "calc(85vh - 120px)" }}>
              {isLoading ? (
                <div className="flex justify-center items-center py-60">
                  <div className="inline-block animate-spin rounded-full h-40 w-40 border-b-3 border-primary-P300"></div>
                </div>
              ) : dayStats ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-12 my-20">
                    <div className="bg-primary-P100 bg-opacity-10 rounded-2xl p-16 border border-primary-P100">
                      <p className="text-10 text-primary-P500 font-semibold mb-6 tracking-wide">
                        TOTAL
                      </p>
                      <p className="text-28 font-bold text-primary-P500">
                        {dayStats.totalOrders}
                      </p>
                      <p className="text-10 text-primary-P300 mt-2">orders</p>
                    </div>
                    <div className="bg-green-G300 bg-opacity-10 rounded-2xl p-16 border border-green-G300">
                      <p className="text-10 text-green-G300 font-semibold mb-6 tracking-wide">
                        DONE
                      </p>
                      <p className="text-28 font-bold text-green-G300">
                        {dayStats.stats.find((s) => s.status === "done")?.count || 0}
                      </p>
                      <p className="text-10 text-green-G200 mt-2">completed</p>
                    </div>
                    <div className="bg-warning-W200 bg-opacity-10 rounded-2xl p-16 border border-warning-W300">
                      <p className="text-10 text-warning-W500 font-semibold mb-6 tracking-wide">
                        ACTIVE
                      </p>
                      <p className="text-28 font-bold text-warning-W500">
                        {(dayStats.stats.find((s) => s.status === "ordered")?.count || 0) +
                          (dayStats.stats.find((s) => s.status === "in-progress")?.count || 0)}
                      </p>
                      <p className="text-10 text-warning-W400 mt-2">in process</p>
                    </div>
                  </div>

                  {/* Progress Bars */}
                  <div className="space-y-16 mt-24">
                    <h4 className="text-14 font-bold text-primary-onligth mb-16">
                      Status Breakdown
                    </h4>
                    {dayStats.stats.map((stat) => {
                      const percentage = dayStats.totalOrders > 0 
                        ? (stat.count / dayStats.totalOrders) * 100 
                        : 0;
                      
                      return (
                        <div key={stat.status} className="space-y-8">
                          {/* Label Row */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-10">
                              <div
                                className="flex items-center justify-center w-24 h-24 rounded-lg"
                                style={{ backgroundColor: stat.bgColor }}
                              >
                                <div style={{ color: stat.color, transform: "scale(0.85)" }}>
                                  {stat.icon}
                                </div>
                              </div>
                              <span className="text-13 font-semibold text-primary-onligth">
                                {stat.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-12">
                              <span
                                className="text-16 font-bold min-w-[30px] text-right"
                                style={{ color: stat.color }}
                              >
                                {stat.count}
                              </span>
                              <span className="text-12 text-secondary-S700 min-w-[40px] text-right">
                                {percentage.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="w-full bg-netural-N30 rounded-full h-8 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500 ease-out"
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: stat.color,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Refresh Button */}
                  <button
                    onClick={fetchDailyStats}
                    disabled={isLoading}
                    className="mt-24 w-full py-14 px-16 bg-primary-P500 hover:bg-primary-P400 text-white rounded-xl text-14 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center justify-center gap-10">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={isLoading ? "animate-spin" : ""}
                      >
                        <path
                          d="M14.5 8C14.5 4.41015 11.5899 1.5 8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span>Refresh Data</span>
                    </div>
                  </button>
                </>
              ) : (
                <div className="text-center py-60">
                  <p className="text-16 text-secondary-S700">No data available</p>
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes slideUp {
              from {
                transform: translateY(100%);
              }
              to {
                transform: translateY(0);
              }
            }
          `}</style>
        </>
      )}
    </>
  );
};

export default MiniDashboard;

